import React from 'react';

/**
 * EverBook Logo — official brand mark (raster asset) + Playfair Display 500 wordmark.
 *
 * Mark is the canonical artwork at /brand/everbook-mark-official.png, rendered
 * with intrinsic aspect ratio preserved (no stretching, no recolor, no path
 * reconstruction). Wordmark: cream #d8c19c by default, white on variant='white'.
 *
 * Props:
 *   size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'  (default 'md')
 *   showText: boolean (default true) — has no effect if markOnly is true
 *   showTagline: boolean (default false)
 *   markOnly: boolean (default false) — renders only the mark, no wordmark
 *   variant: 'default' | 'white'   ('white' keeps wordmark white for hi-contrast
 *                                   against busy/photographic backgrounds)
 *   className: string
 */

const MARK_HEIGHTS = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
};

const TAGLINE_SIZES = {
  xs: 9,
  sm: 11,
  md: 12,
  lg: 14,
  xl: 16,
};

// Official mark artwork. Intrinsic aspect ratio of the trimmed asset (w/h).
const MARK_ASSET = '/brand/everbook-mark-official.png';
const MARK_ASPECT = 576 / 359;

function MarkImg({ height }) {
  const width = height * MARK_ASPECT;
  return (
    <img
      src={MARK_ASSET}
      alt="EverBook"
      width={width}
      height={height}
      style={{
        display: 'block',
        flexShrink: 0,
        height: `${height}px`,
        width: `${width}px`,
        objectFit: 'contain',
      }}
    />
  );
}

export default function Logo({
  size = 'md',
  showText = true,
  showTagline = false,
  markOnly = false,
  variant = 'default',
  className = '',
}) {
  const markH = MARK_HEIGHTS[size] || MARK_HEIGHTS.md;

  if (markOnly) {
    return (
      <span
        className={`inline-flex items-center ${className}`}
        style={{ lineHeight: 1 }}
      >
        <MarkImg height={markH} />
      </span>
    );
  }

  // Playfair cap-height ≈ 0.71 × em-size. We want cap-height = mark height.
  const wordEm = markH / 0.71;
  const markW = markH * MARK_ASPECT;
  const gap = markW * 0.5;
  const tagSize = TAGLINE_SIZES[size] || TAGLINE_SIZES.md;
  const wordmarkColor = variant === 'white' ? '#FFFFFF' : '#d8c19c';

  return (
    <div
      className={`inline-flex items-baseline ${className}`}
      style={{ gap: `${gap}px`, lineHeight: 1 }}
    >
      <span style={{ alignSelf: 'flex-end', display: 'inline-flex' }}>
        <MarkImg height={markH} />
      </span>

      {showText && (
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <span
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 500,
              fontSize: `${wordEm}px`,
              color: wordmarkColor,
              letterSpacing: '-0.01em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            EverBook
          </span>
          {showTagline && (
            <span
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontSize: `${tagSize}px`,
                color: '#B8B8B8',
                letterSpacing: '0.02em',
                lineHeight: 1.2,
                marginTop: '2px',
                whiteSpace: 'nowrap',
              }}
            >
              Your Life Story
            </span>
          )}
        </span>
      )}
    </div>
  );
}

export function LogoIcon({ size = 'md', className = '' }) {
  const markH = MARK_HEIGHTS[size] || MARK_HEIGHTS.md;
  return (
    <span className={`inline-flex ${className}`} style={{ lineHeight: 1 }}>
      <MarkImg height={markH} />
    </span>
  );
}
